import styled from "@emotion/styled";
import { ChangeEvent, KeyboardEvent, ReactNode, useRef, Ref, InputHTMLAttributes, FocusEvent } from "react";
import { BaseInput, baseInputClasses } from "@kaltura/ds-react-components";
import { Search24Icon } from "@kaltura/ds-react-icons";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface KeywordSearchProps {
    /**
     * current search keyword
     * (presentation only)
     */
    value?: string;

    placeholder?: string;

    /**
     * search input aria-label
     * mandatory because search input has no visual label
     */
    ariaLabel: string;

    /**
     * submit search callback
     */
    onSubmit: (value: string) => void;

    /**
     * keyword change callback
     * (for use with search suggestions)
     */
    onChange?: (value: string) => void;

    /**
     * key down(arrow right) callback
     */
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;

    /**
     * on focus
     */
    onFocus?: () => void;

    /**
     * end InputAdornment for this component
     */
    endAdornment?: ReactNode;

    /**
     * input props from the autocomplete hook (optional)
     */
    inputProps?: InputHTMLAttributes<HTMLInputElement>;

    /**
     * ref passed to the input element (optional)
     * used for autocomplete
     */
    inputRef?: Ref<HTMLInputElement>;

    /**
     * should search input take full screen width
     */
    fullWidth?: boolean;
}

const StyledKeywordSearch = styled(
    BaseInput,
    {
        shouldForwardProp(propName) {
            return propName !== "fullWidth";
        },
    }
)<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    width: fullWidth ? "100%" : 205,
    borderColor: theme.kaltura.palette.tone4,
    [`&.${baseInputClasses.adornedStart}, &.${baseInputClasses.adornedEnd}`]: {
        svg: {
            color: theme.kaltura.palette.tone3,
        },
    },
    [`&.${baseInputClasses.adornedStart}`]: {
        svg: {
            marginRight: 0,
        },
    },
    "& input::-webkit-search-cancel-button": {
        display: "none",
    },
}));

/**
 * search bar input field.
 *
 * when having search suggestions, some of the events on the input
 * actualy relates to the suggestions (arrow up/down, right/left, blur)
 */
export function KeywordSearch({
    value = "",
    placeholder,
    ariaLabel,
    inputProps,
    inputRef,
    onSubmit,
    onChange,
    onKeyDown,
    onFocus,
    endAdornment,
    fullWidth,
}: KeywordSearchProps) {
    const activeRef = useRef<boolean>(false);
    const sendButtonAnalytics = useButtonAnalytics();

    /**
     * notify of value change without submit
     * @param event
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(event.target.value);
        }
    };

    /**
     * submit search on enter key
     */
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowRight") {
            activeRef.current = true;
            onKeyDown?.(event);
            return;
        }

        if (event.key === "Enter") {
            sendButtonAnalytics("Search", ButtonClickAnalyticsType.SEARCH);
            onSubmit(String(inputProps?.value ?? value));
        }
    };

    /**
     * focusing on a search suggestion(see ArrowRight above) cause a blur event
     * on the keyboard search input. deal with it.
     */
    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        const selectionActive = activeRef.current;
        activeRef.current = false;

        // on a search suggestion - supress the blur event
        selectionActive && event.stopPropagation();
        selectionActive && event.preventDefault();

        // not a search suggestion - continue with stock event
        !selectionActive && inputProps?.onBlur?.(event);
    };

    /**
     * inform the searchbar of the focus, to reset the clickAwayListener.
     * needed for loosing focus when a search suggestion is focused - thus
     * blocking the blur event(see above).
     */
    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        onFocus?.();
        inputProps?.onFocus?.(event);
        sendButtonAnalytics("Focus Search Input", ButtonClickAnalyticsType.SEARCH);
    };

    return (
        <StyledKeywordSearch
            placeholder={placeholder}
            value={inputProps?.value ?? value}
            startAdornment={<Search24Icon />}
            endAdornment={endAdornment}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            className="kms-ds-keyword-search"
            inputRef={inputRef}
            inputProps={{
                type: "search",
                "aria-label": ariaLabel,
                ...inputProps,
                onBlur: handleBlur,
                onFocus: handleFocus,
            }}
            fullWidth={fullWidth}
        />
    );
}

export default KeywordSearch;
